import { Grid, Paper, List, ListItem, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import Link from '@mui/material/Link';
import packageInfo from '../../../package.json';
import ConnectionStatus from 'features/spectrasWebSocketServer/ConnectionStatus';
import { useVersion } from './api/getVersion';
import semver from 'semver';
import { formatInTimeZone } from 'date-fns-tz';
import { useUserDateTime } from '../timezone/hooks/useUserDateTime';
import { usePurgeSettings } from './api/getPurgeSettings';
import { useUsers } from '../userManagement/api/getUsers';
import { useAppSelector } from '../../app/hooks';
import { useIsAdmin } from 'features/auth/hooks/useIsAdmin';
import { Divider } from '@mui/material';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';

interface Props {
  isNav: boolean;
}

const ConnectionStatusWidget = ({ isNav }: Props) => {
  const isAdmin = useIsAdmin();
  let version = packageInfo.version;
  const { dateFormat, timezone } = useUserDateTime();
  const [versionError, setVersionFlag] = useState(false);
  const { data: currentVersion } = useVersion({});
  let buildDate = formatInTimeZone(
    new Date(packageInfo.buildDate),
    timezone,
    dateFormat
  );

  useEffect(() => {
    if (version && currentVersion) {
      const validation = semver.lt(version, currentVersion);
      setVersionFlag(validation);
    }
  }, [version, currentVersion]);

  const { data: purgeSettings } = usePurgeSettings({});
  const connectedUserIds = useAppSelector(
    (state) => state.spectrasWebSocketServer.connectedUsers
  );
  const { data: users } = useUsers({});
  const connectedUsers = users?.filter((user) =>
    connectedUserIds.includes(user.id)
  );

  return !isNav ? (
    <Grid container component={Paper}>
      <Grid
        item
        xs={12}
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Grid item>
          <TroubleshootIcon style={{ verticalAlign: 'middle' }} />
        </Grid>
        <Typography variant='h6' style={{ padding: '0rem 0rem 0rem 0.3rem' }}>
          Software Information
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>

      <Grid
        item
        xs={6}
        style={{
          borderRight: '1px solid rgba(0, 0, 0, 0.12)',
          padding: '0.1rem 0.1rem',
        }}
      >
        <List>
          <ListItem>
            <Typography
              variant='h5'
              style={{ fontWeight: 'bold', color: '#3f51b5' }}
            >
              Server Info
            </Typography>
          </ListItem>
          <ListItem>
            <ConnectionStatus />
          </ListItem>
          <ListItem>
            <Typography variant='subtitle2'>
              Purging{' '}
              {purgeSettings?.purge_enabled
                ? `Enabled (${
                    purgeSettings?.purge_interval[0] === '1'
                      ? purgeSettings.purge_interval.slice(0, -1)
                      : purgeSettings.purge_interval
                  })`
                : 'Disabled'}
            </Typography>
          </ListItem>
          {isAdmin && (
            <ListItem>
              <Grid container direction='column'>
                <Typography variant='subtitle2' sx={{ minWidth: '6rem' }}>
                  Active Users:
                </Typography>
                {connectedUsers?.map((user) => (
                  <Grid item sx={{ pl: '2rem' }} key={user.id}>
                    <Typography variant='subtitle2'>{user.name}</Typography>
                  </Grid>
                ))}
              </Grid>
            </ListItem>
          )}
        </List>
      </Grid>

      <Grid item xs={6} style={{ padding: '0.1rem 0.1rem' }}>
        <List>
          <ListItem>
            <Typography
              variant='h5'
              style={{ fontWeight: 'bold', color: '#3f51b5' }}
            >
              Version
            </Typography>
          </ListItem>
          <ListItem>Build Version: {version}</ListItem>
          {versionError === true && (
            <ListItem>
              <Typography variant='subtitle1' color='secondary'>
                <Link
                  target='_blank'
                  href='https://portal.lptapps.com/'
                  color='inherit'
                >
                  <b>New Version Available: {currentVersion}</b>
                </Link>
              </Typography>
            </ListItem>
          )}
          <ListItem>Build Date: {buildDate}</ListItem>
        </List>
      </Grid>
    </Grid>
  ) : (
    <Grid container item alignItems='right' justifyContent='right' xs={12}>
      <Typography variant='subtitle2' style={{ marginRight: '3px' }}>
        Server Status:
      </Typography>
      <ConnectionStatus />
    </Grid>
  );
};

export default ConnectionStatusWidget;
